import * as api from '@sloyd/api';
import { delay, invokeEvent, l } from 'shared-base';
import { RealtimeEventServer } from './realtime.types';

let dc: RTCDataChannel;
let pc: RTCPeerConnection;
let localStream: MediaStream;

export async function functionResponse(callId: string, params: Json) {
  const responseCreate = {
    type: 'conversation.item.create',
    item: {
      type: 'function_call_output',
      call_id: callId,
      output: JSON.stringify(params),
    },
  };
  dc.send(JSON.stringify(responseCreate));

  await delay(10);

  await respond(
    "Reply based on the function's output. Speak in future tense. Be informal and very concise like 'on it' but add a bit"
  );
}

export async function respond(instructions: string) {
  const responseReply = {
    type: 'response.create',
    response: {
      instructions,
    },
  };

  dc.send(JSON.stringify(responseReply));
}

export async function changeInstructions(instructions: string, voice?: Voice) {
  const session: any = {
    instructions,
  };

  if (voice) {
    session.voice = voice;
  }

  const sessionUpdate = {
    type: 'session.update',
    session,
  };

  dc.send(JSON.stringify(sessionUpdate));
}

export async function changeVoice(voice: Voice) {
  const sessionUpdate = {
    type: 'session.update',
    session: {
      voice,
    },
  };
  dc.send(JSON.stringify(sessionUpdate));
}

export async function send(message: string, silent = false) {
  if (!dc) return;

  const responseUpdate = {
    type: 'conversation.item.create',
    item: {
      type: 'message',
      role: 'user',
      content: [
        {
          type: 'input_text',
          text: message,
        },
      ],
    },
  };
  dc.send(JSON.stringify(responseUpdate));

  if (silent) {
    return;
  }

  await delay(10);

  await respond('Reply');
}

const ignoreTypes: RealtimeEventServer[] = [
  'response.text.delta',
  'response.audio.delta',
  'response.audio_transcript.delta',
  'response.function_call_arguments.delta',
];

export async function init(token: string) {
  // Get an ephemeral key from your server

  const data = await api.ai.realtime(token);
  const EPHEMERAL_KEY = data.client_secret.value;

  // Create a peer connection
  pc = new RTCPeerConnection();

  // Set up to play remote audio from the model
  const audioEl = document.createElement('audio');
  audioEl.autoplay = true;
  pc.ontrack = (e) => (audioEl.srcObject = e.streams[0]);

  // Add local audio track for microphone input in the browser
  localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  pc.addTrack(localStream.getTracks()[0]);

  // Set up data channel
  dc = pc.createDataChannel('oai-events');

  dc.addEventListener('open', () => {
    console.log('Data channel open');

    invokeEvent('REALTIME', {
      verb: 'ready',
    });

    send('Hello!');
  });

  dc.addEventListener('message', (e: MessageEvent) => {
    const realtimeEvent = JSON.parse(e.data);

    if (ignoreTypes.includes(realtimeEvent.type)) {
      return;
    }

    l(`rt|${realtimeEvent.type}`, realtimeEvent, true);

    switch (realtimeEvent.type) {
      case 'response.audio_transcript.done': {
        const { transcript } = realtimeEvent;
        invokeEvent('REALTIME', {
          verb: 'transcript',
          content: transcript,
        });
        break;
      }
      case 'response.function_call_arguments.done': {
        const { name, call_id } = realtimeEvent;
        const params = JSON.parse(realtimeEvent.arguments);

        invokeEvent('REALTIME', {
          verb: 'functionStart',
          name,
          call_id,
          params,
        });
        break;
      }
    }
  });

  // Start the session
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  const baseUrl = 'https://api.openai.com/v1/realtime';
  const model = 'gpt-4o-realtime-preview-2024-12-17';
  const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
    method: 'POST',
    body: offer.sdp,
    headers: {
      Authorization: `Bearer ${EPHEMERAL_KEY}`,
      'Content-Type': 'application/sdp',
    },
  });

  const answer = {
    type: 'answer',
    sdp: await sdpResponse.text(),
  };
  await pc.setRemoteDescription(answer);
}

export async function stop(silent: boolean) {
  if (!silent) {
    await send('Bye bye!');
  }

  await delay(3000);

  console.log('Stopping session...');
  dc?.close();
  pc?.close();
  localStream?.getTracks().forEach((track) => track.stop());
}
